import { getSessionID, sendOTP } from '@/apis/login/login.api';
import { AccountRiskType } from '@/apis/login/login.interface';
import UKToast from '@/components/UKToast';
import loginConfig from '@/intl-jdi-common-ssr/constants/loginConfig';
import { loadRecaptchaScript } from '@/utils/recaptcha';
import { isFunction } from 'lodash';
import JDCloudSlideUtils from './JDCloudSlideUtils';

import { LOGIN_EVENT_NAMES, loginBusinessLogger } from '@/utils/login/loginLogger';

type Action = () => void;
type Function = (code: string) => void;
type ParamFunction = (param: {}) => void;

interface SessionProps {
  riskType: number;
  email?: string;
  mobile?: string;
  mobilePrefix?: string;
}

interface RiskProps {
  t: (key: string) => string;
  riskType: number;
  sessionParam: SessionProps;
  isPhone: boolean;
  action?: string;
  onSliderLoading: Action;
  onCancelLoading: Action;
  onSliderSuccess: ParamFunction;
  onSliderFail: Action;
  onSliderCancel: Action;
  onSliderLoad: Action;
  onVerifySuccess?: ParamFunction | null;
  onSendSuccess: Function;
  onSendFail?: Function;
  onCodeAlreadySend: Function;
}

interface RequestProps {
  riskType: number;
  otpType: number;
  idPrefix?: string;
  identifier: string;
  scene: number;
  captchaAction?: string;
  captchaCode?: string;
}

// 开启风控
export const openRiskControl = async (riskData: RiskProps, requestData: RequestProps) => {
  const { riskType, isPhone } = riskData || {};
  if (riskType === AccountRiskType.White) {
    riskCallback(riskData, requestData, { riskType });
  } else if (riskType === AccountRiskType.Google) {
    if (isPhone) {
      const onOpenGoogle = () => {
        openGoogleRisk(riskData, requestData, onOpenGoogle);
      };
      onOpenGoogle();
    } else {
      riskCallback(riskData, requestData, { riskType });
    }
  } else if (riskType === AccountRiskType.Joybuy) {
    const onOpenJoybuy = () => {
      openJoybuyRisk(riskData, requestData, onOpenJoybuy);
    };
    onOpenJoybuy();
  }
};

// 直接发送验证码-走风控错误码兜底逻辑
export const forceSendCode = async (riskData: RiskProps, requestData: RequestProps) => {
  const { riskType } = riskData || {};
  const onOpenRisk = () => {
    openRiskControl(riskData, requestData);
  };
  riskCallback(riskData, requestData, { riskType }, onOpenRisk);
};

// 谷歌风控
const openGoogleRisk = async (
  riskData: RiskProps,
  requestData: RequestProps,
  onOpenRisk: Action,
) => {
  const w: any = window;
  const { action, t } = riskData || {};
  try {
    await loadRecaptchaScript();
    w?.grecaptcha.enterprise.ready(async () => {
      const token = await w?.grecaptcha.enterprise.execute(loginConfig.GoogleReCAPTCHAEnum.KEY_ID, {
        action,
      });
      requestData.captchaCode = token;
      sendOTPCode(riskData, requestData, onOpenRisk);
    });
  } catch (err) {
    UKToast.show({
      icon: 'error',
      content: t('login.default.rules.google'),
    });
    return;
  }
};

// 滑块风控
const openJoybuyRisk = async (riskData: RiskProps, requestData: RequestProps, callback: Action) => {
  const {
    sessionParam,
    t,
    onSliderLoading,
    onCancelLoading,
    onSliderSuccess,
    onSliderFail,
    onSliderCancel,
    onSliderLoad,
  } = riskData || {};
  if (isFunction(onSliderLoading)) {
    onSliderLoading();
  }
  const res = await getSessionID(sessionParam);
  if (res && res.data && res.code === '200') {
    const sessionId = res.data.sessionId;
    if (sessionId && !sessionId.startsWith(loginConfig.emailSliderSessionHeader)) {
      JDCloudSlideUtils.getInstance(t).initJDSlide(
        sessionId,
        (token: string) => {
          // 成功回调
          console.log('onSliderSuccess');
          const tokenParam = {
            sessionId,
            captchaCode: token,
          };
          riskCallback(riskData, requestData, tokenParam, callback);
          if (isFunction(onSliderSuccess)) {
            onSliderSuccess(token);
          }
        },
        (message: string) => {
          // 失败回调
          console.log('onSliderFail: ' + message);
          if (isFunction(onSliderFail)) {
            onSliderFail();
          }
        },
        () => {
          // 取消回调
          console.log('onSliderCancel');
          if (isFunction(onSliderCancel)) {
            onSliderCancel();
          }
        },
        () => {
          // SDK加载完成回调
          console.log('onSliderLoad');
          if (isFunction(onSliderLoad)) {
            onSliderLoad();
          }
        },
      );
    } else {
      // 不风控
      const tokenParam = {
        sessionId,
        captchaCode: '',
      };
      riskCallback(riskData, requestData, tokenParam, callback);
      if (isFunction(onCancelLoading)) {
        onCancelLoading();
      }
    }
  } else {
    console.log('get session id fail');
    if (isFunction(onCancelLoading)) {
      onCancelLoading();
    }
  }
};

// 风控回调
const riskCallback = (
  riskData: RiskProps,
  requestData: RequestProps,
  tokenParam: any,
  onOpenRisk: Action | null = null,
) => {
  const { onVerifySuccess } = riskData || {};
  if (onVerifySuccess && isFunction(onVerifySuccess)) {
    onVerifySuccess(tokenParam);
  } else {
    const param = { ...requestData, ...tokenParam };
    sendOTPCode(riskData, param, onOpenRisk);
  }
};

// 发送验证码
const sendOTPCode = async (riskData: RiskProps, params: any, onOpenRisk: Action | null = null) => {
  try {
    loginBusinessLogger({
      eventName: LOGIN_EVENT_NAMES.SEND_CODE,
      message: {
        params,
      },
    });
  } catch (error) {}
  try {
    const [_, res]: any = await sendOTP(params);
    const { t, onSendSuccess, onSendFail, onCodeAlreadySend } = riskData || {};
    if (res) {
      if (res.success) {
        UKToast.show({
          content: t('login.common.sendSuccess'),
          icon: 'success',
        });
        if (isFunction(onSendSuccess)) {
          onSendSuccess(res.data);
        }
      } else if (
        res.code === loginConfig.RiskEnum.CODE_ALREADY_SEND &&
        isFunction(onCodeAlreadySend)
      ) {
        onCodeAlreadySend(res?.msg);
      } else if (canOpenJoybuyRisk(parseInt(res.code)) && isFunction(onOpenRisk)) {
        onOpenRisk && onOpenRisk();
      } else if (canOpenGoogleRisk(parseInt(res.code)) && isFunction(onOpenRisk)) {
        onOpenRisk && onOpenRisk();
      } else {
        if (onSendFail && isFunction(onSendFail)) {
          onSendFail(res?.msg);
        }
        if (res.msg) {
          UKToast.show({
            content: res.msg,
            icon: 'error',
          });
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
};

export const canOpenJoybuyRisk = (code: number) => {
  return (
    code &&
    code >= loginConfig.RiskEnum.CODE_JOYBUY_RISK_MIN &&
    code <= loginConfig.RiskEnum.CODE_JOYBUY_RISK_MAX
  );
};

export const canOpenGoogleRisk = (code: number) => {
  return (
    code >= loginConfig.RiskEnum.CODE_GOOGLE_RISK_MIN &&
    code <= loginConfig.RiskEnum.CODE_GOOGLE_RISK_MAX
  );
};
